import { Button } from '@components/ui/button/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@components/ui/shadcn/dialog';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { CoverageRuleModel } from '../../models/CoverageRule';

interface DeleteCoverageRuleModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    rule: CoverageRuleModel | null;
    isDeleting?: boolean;
}

export const DeleteCoverageRuleModal = ({
    isOpen,
    onClose,
    onConfirm,
    rule,
    isDeleting = false,
}: DeleteCoverageRuleModalProps) => {
    const { t } = useTranslation('coverageZonesModule');

    if (!rule) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md transition-colors">
                <DialogHeader>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10">
                            <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className="h-6 w-6 text-destructive"
                            />
                        </div>
                        <DialogTitle className="text-foreground">{t('delete.title')}</DialogTitle>
                    </div>
                    <DialogDescription className="text-muted-foreground">
                        {t('delete.message')}
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4 px-4 bg-muted/50 rounded-lg space-y-2">
                    <div className="text-sm">
                        <span className="font-semibold text-foreground">ID:</span>
                        <span className="ml-2 text-muted-foreground">{rule.id}</span>
                    </div>
                    <div className="text-sm">
                        <span className="font-semibold text-foreground">{t('form.priority')}:</span>
                        <span className="ml-2 text-muted-foreground">{rule.priority}</span>
                    </div>
                    {rule.cp_from && rule.cp_to && (
                        <div className="text-sm">
                            <span className="font-semibold text-foreground">CP:</span>
                            <span className="ml-2 text-muted-foreground">
                                {rule.cp_from} - {rule.cp_to}
                            </span>
                        </div>
                    )}
                    <div className="text-sm">
                        <span className="font-semibold text-foreground">{t('form.type')}:</span>
                        <span className="ml-2 text-muted-foreground">
                            {rule.rule === 'allow' ? t('form.typeAllow') : t('form.typeDeny')}
                        </span>
                    </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={isDeleting}
                        className="transition-colors"
                    >
                        {t('delete.cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={onConfirm}
                        disabled={isDeleting}
                        className="bg-destructive hover:bg-destructive/90 transition-colors"
                    >
                        {isDeleting ? 'Eliminando...' : t('delete.confirm')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

